@use '@/styles/tokens' as *;

/* Figma 594:532395 (expanded) / 594:533307 (collapsed) — a white card docked at the canvas's
 * top-inline-start, sharing the inset `<MapPage>`'s `.topControls` and `MAP_CONTROL_LAYOUT` use.
 * Absolute inside the map AREA, not the map, so it does not move when the Grower pans.
 *
 * D-01: `top`/`inset-inline-start` match `.topControls`'s own two numbers exactly (99px / 82px,
 * Figma's measurement against the full-bleed frame, which this route now renders as) — this panel
 * sits directly below where that row sits on the Map, and both would otherwise render half behind
 * the now-floating `<TopNav>` pill.
 *
 * Collapsed and expanded are the SAME element: only the body is dropped, so nothing about the card
 * has to be re-styled for a state change (and no marked Point can be disturbed by one). */
.panel {
  /* A flex column so `.body` below can resolve `flex: 1` against the panel's own `max-height` —
   * which is what lets the SECTION inside it scroll its rows while keeping its header and actions
   * in view (ticket 09). */
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 99px;
  inset-inline-start: 82px;
  z-index: 2;
  /* Figma 405px. A fixed panel width — no TS constant for it (unlike `FIELD_PANEL_SIZE`): nothing
   * outside this stylesheet needs the number yet. Add one if a future ticket does. */
  width: 405px;
  max-height: calc(100% - var(--space-8));
  padding-block: var(--space-4);
  border-radius: $radius-md;
  background: var(--surface-card);
  box-shadow: var(--shadow-surface);
}

/* Figma 594:532396 — pb-12 / pl-10 / pr-12. `--space-3` is 12; the 2px on the inline-start inset is
 * within the token scale's own step and not worth an off-scale value. */
.titleBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--space-3) var(--space-3);
}

/* Figma draws the rule only under an EXPANDED title bar (594:533307 has none). */
.expanded .titleBar {
  border-bottom: 1px solid var(--border-subtle);
}

.title {
  margin: 0;
  font-size: $font-size-16;
  font-weight: $font-weight-regular;
  color: var(--text-primary);
}

.chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
}

/* Figma 594:532399 — the caret is rotated 180° while the panel is open, so it points at what the
 * press does. */
.expanded .chevron {
  transform: rotate(180deg);
}

/* Figma 594:532400 — px-12. The scroll moved DOWN, into the section: ticket 04 scrolled the whole
 * body, which would have carried ticket 09's Co-ordinates header and its Cancel/Save out of view.
 * The title bar still never leaves view — it is a sibling above this box, not inside it. */
/* QA round 2 D6: Figma (`594:532395`, `594:530535`, `598:539901`) gives the body a DEFINITE 559px
 * height in every state — polygon or circle, one Point or fifty — which is what pins Cancel/Save at
 * the card's foot instead of riding up under the last row. `flex: 0 1 559px`, not `flex: 1`: a
 * definite basis rather than one resolving to content height, while `flex-shrink: 1` still lets it
 * give way under the panel's own `max-height` on a short viewport (Save must stay reachable). */
.body {
  display: flex;
  flex: 0 1 559px;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  padding-inline: var(--space-3);
}
